import moment from 'moment'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import { utils, writeFile } from 'xlsx'
import { Workbook } from 'exceljs'
import { InventoryItemDto } from '../interfaces/Inventory/InventoryItemDto'
import logo from '../assets/images/TEALCA-logo.jpeg'
import {
  UserInterface,
  ShipmentInterface,
  DestinationInterface,
  DispatcherCCWInterface,
  FavoriteDestinationsInterfase,
  StoreShipmentInterface,
} from '../interfaces'
import { shipmentStatusFormat } from './format'

const toSentenceCase = (str: string) => {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

const toTitleCase = (str: string) => {
  if (!str) return ''
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export const exportDestinationsPDF = (
  user: UserInterface,
  destinations: DestinationInterface[]
) => {
  const doc = new jsPDF()

  // Logo
  let img = new Image()
  img.src = logo
  doc.addImage(img, 'JPEG', 13, 13, 25, 20)

  // Title
  doc.setFontSize(16).setFont('helvetica', 'bold')
  doc.text('REPORTE DE', 87, 20)
  doc.text('DESTINATARIO', 84, 26)

  // Date
  const date = moment()
  doc.setFontSize(10).setFont('helvetica', 'normal')
  doc.text(date.format('DD/MM/YYYY'), 178, 20)
  doc.text(date.format('HH:mm'), 186.5, 24)

  // Destination table
  const columns = [
    'Código destinatario',
    'Alias',
    'Ciudad',
    'Teléfono',
    'Tienda',
    'Persona de accounto',
  ]
  const rows = destinations.map((destination) => [
    destination.location.code ?? '',
    destination.location.name,
    'CCS',
    destination.account.listAccountPhone[0]?.phoneNumber ?? '',
    destination.businessUnit?.buCode,
    destination.account.accountFullName,
  ])
  autoTable(doc, {
    head: [columns],
    body: rows,
    margin: { top: 40, bottom: 30 },
    styles: { halign: 'center', valign: 'middle', fontSize: 7 },
    headStyles: {
      fillColor: [0, 100, 172],
      textColor: 255,
      fontStyle: 'bold',
    },
    bodyStyles: {
      minCellHeight: 16,
      lineWidth: 0.1,
      textColor: 'black',
    },
    didDrawPage: (data) => {
      if (data.pageCount > 0) {
        data.settings.margin.top = 15
        doc.setPage(data.pageCount)
      }
    },
  })

  // Footer
  for (let i = 1; i <= doc.getNumberOfPages(); i++) {
    doc.setPage(i)
    doc.text(`Solicitado por: ${user.login}`, 13, 280)
    doc.text(`${i}`, 195, 280)
  }

  doc.save(`ConsultaDestinatario_${moment().format('DDMMYYYYHHmm')}.pdf`)
}

export const exportDestinationsXLSX = (
  destinations: DestinationInterface[]
) => {
  // New sheet book
  const wb = utils.book_new()

  // Destinations data
  const ws_data = destinations.map((destination) => [
    destination.location.code,
    destination.location.name,
    'CCS',
    destination.account.listAccountPhone[0]?.phoneNumber ?? '',
    destination.businessUnit?.buCode,
    destination.account.accountFullName,
  ])

  // Add header
  ws_data.unshift([
    'Código destinatario',
    'Alias',
    'Ciudad',
    'Teléfono',
    'Tienda',
    'Persona de accounto',
  ])

  const ws = utils.aoa_to_sheet(ws_data)

  utils.book_append_sheet(wb, ws, 'Destinations')

  // Export
  writeFile(wb, `ConsultaDestinatario_${moment().format('DDMMYYYYHHmm')}.xlsx`)
}

export const exportDestinationsTXT = (destinations: DestinationInterface[]) => {
  let txt = ''

  for (let i = 0; i < destinations.length; i++) {
    const destination = destinations[i]
    const identificationType = destination.account.abbreviationName

    txt += `"DestinationId":"${destination.id}",\n`
    txt += `"DestinationCod":"${destination.location.code}",\n`
    txt += `"Description":"${destination.location.name}",\n`
    txt += `"Alias":"${destination.location.name}",\n`
    txt += `"AddressFull":"${destination.location.address}",\n`
    txt += `"City":"CCS",\n`
    txt += `"Country":"VZL",\n`
    txt += `"Phone":"${destination.account.listAccountPhone[0]?.phoneNumber}",\n`
    txt += `"Phone2":"${destination.account.listAccountPhone[1]?.phoneNumber}",\n`
    txt += `"Email":"${destination.account.listAccountEmail[0]?.email}",\n`
    txt += `"OfficeCod":"",\n`
    txt += `"NameContact":"${destination.account.accountFullName}",\n`
    txt += `"DestinationDocument":"${identificationType}${destination.account.identificationNumber}",\n`
    txt += `"DocumentType":"${identificationType.slice(0, 1)}",\n`
    txt += `"NameContact":"${destination.account.identificationNumber}"`
    txt += i < destinations.length - 1 ? ',\n\n' : '\n\n\n'
  }

  let element = document.createElement('a')
  element.setAttribute(
    'href',
    'data:text/plain;charset=utf-8,' + encodeURIComponent(txt)
  )
  element.setAttribute(
    'download',
    `ConsultaDestinatario_${moment().format('DDMMYYYYHHmm')}.txt`
  )

  element.style.display = 'none'
  document.body.appendChild(element)

  element.click()

  document.body.removeChild(element)
}

//////////Export Shipments

export const exportShipmentsPDF = (
  user: UserInterface,
  shipments: StoreShipmentInterface[]
) => {
  const doc = new jsPDF()

  // Logo
  let img = new Image()
  img.src = logo
  doc.addImage(img, 'JPEG', 13, 13, 25, 20)

  // Title
  doc.setFontSize(16).setFont('helvetica', 'bold')
  doc.text('REPORTE DE ENVIO', 80, 25)
  // Date
  const date = moment()
  doc.setFontSize(10).setFont('helvetica', 'normal')
  doc.text(date.format('DD/MM/YYYY'), 178, 20)
  doc.text(date.format('HH:mm'), 186.5, 24)

  // Destination table
  const columns = [
    'Guía',
    'Fecha de Creación',
    'Nº de Piezas',
    'Peso',
    'Nombre Destinatario',
    'Teléfono',
    'Ciudad',
    'Entrega',
    'Guía Aliada',
    'Estatus',
  ]
  const rows = shipments.map((shipment) => [
    shipment.shipmentNumber,
    moment(shipment.creationDate).format('DD/MM/YYYY'),
    shipment.totalPieces ?? 0,
    shipment.totalChargedWeight ?? 0,
    toTitleCase(shipment.consigneeName),
    shipment.consigneePhone ?? '',
    shipment.locationName ?? '',
    shipment.storePickup === '1'
      ? (shipment.buCodeConsignee || '') +
        ' - ' +
        toSentenceCase(shipment.consigneeBUAreaName || '')
      : 'A domicilio',
    shipment.shipmentReference ?? '',
    toSentenceCase(shipmentStatusFormat(shipment?.statusID)),
  ])
  autoTable(doc, {
    head: [columns],
    body: rows,
    margin: { top: 45, bottom: 30 },
    styles: { halign: 'center', valign: 'middle', fontSize: 7 },
    headStyles: {
      fillColor: [0, 100, 172],
      textColor: 255,
      fontStyle: 'bold',
      minCellHeight: 11,
    },
    bodyStyles: {
      minCellHeight: 16,
      lineWidth: 0.1,
      textColor: 'black',
    },
    didDrawPage: (data) => {
      if (data.pageCount > 0) {
        data.settings.margin.top = 15
        doc.setPage(data.pageCount)
      }
    },
  })

  // Footer
  for (let i = 1; i <= doc.getNumberOfPages(); i++) {
    doc.setPage(i)
    doc.text(`Solicitado por: ${user.login}`, 13, 280)
    doc.text(`${i}`, 195, 280)
  }

  doc.save(`ConsultaGuia_${moment().format('DDMMYYYYHHmm')}.pdf`)
}

export const exportShipmentsXLSX = (shipments: StoreShipmentInterface[]) => {
  // New sheet book
  const wb = utils.book_new()

  // Destinations data
  const ws_data = shipments.map((shipment) => [
    shipment.shipmentNumber,
    moment(shipment.creationDate).format('DD/MM/YYYY'),
    shipment.totalPieces ?? 0,
    shipment.totalChargedWeight ?? 0,
    toTitleCase(shipment.consigneeName),
    shipment.consigneePhone ?? '',
    shipment.locationName ?? '',
    shipment.storePickup === '1'
      ? (shipment.buCodeConsignee || '') +
        ' - ' +
        toSentenceCase(shipment.consigneeBUAreaName || '')
      : 'A domicilio',
    shipment.shipmentReference ?? '',
    toSentenceCase(shipmentStatusFormat(shipment?.statusID)),
  ])

  // Add header
  ws_data.unshift([
    'Guía',
    'Fecha de Creación',
    'Nº de Piezas',
    'Peso',
    'Nombre Destinatario',
    'Teléfono',
    'Ciudad',
    'Entrega',
    'Guía Aliada',
    'Estatus',
  ])

  const ws = utils.aoa_to_sheet(ws_data)

  utils.book_append_sheet(wb, ws, 'Destinations')

  // Export
  writeFile(wb, `ConsultaGuia_${moment().format('DDMMYYYYHHmm')}.xlsx`)
}

export const exportShipmentsTXT = (shipments: StoreShipmentInterface[]) => {
  let txt = ''

  for (let i = 0; i < shipments.length; i++) {
    const shipment = shipments[i]

    txt += `"GUÍA":"${shipment.shipmentNumber}",\n`
    txt += `"Nº de Piezas":${shipment.totalPieces},\n`
    txt += `"Peso":${shipment.totalChargedWeight},\n`
    txt += `"Nombre Destinatario":"${toTitleCase(shipment.consigneeName)}",\n`
    txt += `"Teléfono":"${shipment.consigneePhone}",\n`
    txt += `"Ciudad":"${shipment.locationName}",\n`
    txt += `"Entrega":"${
      shipment.storePickup === '1'
        ? (shipment.buCodeConsignee?.toUpperCase() || '') +
          ' - ' +
          toSentenceCase(shipment.consigneeBUAreaName?.toUpperCase() || '')
        : 'A domicilio'
    }",\n`
    txt += `"Guía Aliada":"${shipment.shipmentReference}",\n`
    txt += `"Estatus":"${toSentenceCase(
      shipmentStatusFormat(shipment?.statusID)
    )}",\n`
    txt += `"Fecha de Creación":"${moment(shipment.creationDate).format(
      'DD/MM/YYYY'
    )}"`
    txt += i < shipments.length - 1 ? ',\n\n' : '\n\n\n'
  }

  let element = document.createElement('a')
  element.setAttribute(
    'href',
    'data:text/plain;charset=utf-8,' + encodeURIComponent(txt)
  )
  element.setAttribute(
    'download',
    `ConsultaGuia_${moment().format('DDMMYYYYHHmm')}.txt`
  )

  element.style.display = 'none'
  document.body.appendChild(element)

  element.click()

  document.body.removeChild(element)
}

///////////// Export Dispatch
export const exportDispatchPDF = (
  user: UserInterface,
  dispatchs: DispatcherCCWInterface[]
) => {
  const doc = new jsPDF()

  // Logo
  let img = new Image()
  img.src = logo
  doc.addImage(img, 'JPEG', 13, 13, 25, 20)

  // Title
  doc.setFontSize(16).setFont('helvetica', 'bold')
  doc.text('REPORTE DE DESPACHO', 80, 25)
  // Date
  const date = moment()
  doc.setFontSize(10).setFont('helvetica', 'normal')
  doc.text(date.format('DD/MM/YYYY'), 178, 20)
  doc.text(date.format('HH:mm'), 186.5, 24)

  // Destination table
  const columns = [
    'Codigo ',
    'Origen',
    'Destino',
    'Transporte',
    'Conductor',
    'Precinto 1',
    'Precinto 2',
    'Precinto 3',
  ]
  const rows = dispatchs.map((dispatch) => [
    dispatch.masterShipmentHeaderCode,
    dispatch.buCodeSource,
    dispatch.buCodeDestination,
    dispatch.transport,
    dispatch.brandName,
    dispatch.seal1 ?? 'N/D',
    dispatch.seal2 ?? 'N/D',
    dispatch.seal3 ?? 'N/D',
  ])
  autoTable(doc, {
    head: [columns],
    body: rows,
    margin: { top: 45, bottom: 30 },
    styles: { halign: 'center', valign: 'middle', fontSize: 7 },
    headStyles: {
      fillColor: [0, 100, 172],
      textColor: 255,
      fontStyle: 'bold',
      minCellHeight: 11,
    },
    bodyStyles: {
      minCellHeight: 16,
      lineWidth: 0.1,
      textColor: 'black',
    },
    didDrawPage: (data) => {
      if (data.pageCount > 0) {
        data.settings.margin.top = 15
        doc.setPage(data.pageCount)
      }
    },
  })

  // Footer
  for (let i = 1; i <= doc.getNumberOfPages(); i++) {
    doc.setPage(i)
    doc.text(`Solicitado por: ${user.login}`, 13, 280)
    doc.text(`${i}`, 195, 280)
  }

  doc.save(`ConsultaDespacho_${moment().format('DDMMYYYYHHmm')}.pdf`)
}

export const exportDispatchXLSX = (dispatchs: DispatcherCCWInterface[]) => {
  // New sheet book
  const wb = utils.book_new()

  // Destinations data
  const ws_data = dispatchs.map((dispatch) => [
    dispatch.masterShipmentHeaderCode,
    dispatch.buCodeSource,
    dispatch.buCodeDestination,
    dispatch.transport,
    dispatch.brandName,
    dispatch.seal1 ?? 'N/D',
    dispatch.seal2 ?? 'N/D',
    dispatch.seal3 ?? 'N/D',
  ])

  // Add header
  ws_data.unshift([
    'Codigo ',
    'Origen',
    'Destino',
    'Transporte',
    'Conductor',
    'Precinto 1',
    'Precinto 2',
    'Precinto 3',
  ])

  const ws = utils.aoa_to_sheet(ws_data)

  utils.book_append_sheet(wb, ws, 'Destinations')

  // Export
  writeFile(wb, `ConsultaDespacho_${moment().format('DDMMYYYYHHmm')}.xlsx`)
}

export const exportDispatchTXT = (dispatchs: DispatcherCCWInterface[]) => {
  let txt = ''

  for (let i = 0; i < dispatchs.length; i++) {
    const shipment = dispatchs[i]

    txt += `"Codigo":"${shipment.masterShipmentHeaderCode}",\n`
    txt += `"Origen":${shipment.buCodeSource},\n`
    txt += `"Destino":${shipment.buCodeDestination},\n`
    txt += `"Transporte":"${shipment.transport}",\n`
    txt += `"Conductor":"${shipment.brandName}",\n`
    txt += `"Precinto 1":"${shipment.seal1 ?? 'N/D'}",\n`
    txt += `"Precinto 2":"${shipment.seal2 ?? 'N/D'}",\n`
    txt += `"Precinto 3":"${shipment.seal3 ?? 'N/D'}"`
    txt += i < dispatchs.length - 1 ? ',\n\n' : '\n\n\n'
  }

  let element = document.createElement('a')
  element.setAttribute(
    'href',
    'data:text/plain;charset=utf-8,' + encodeURIComponent(txt)
  )
  element.setAttribute(
    'download',
    `ConsultaDestinatario_${moment().format('DDMMYYYYHHmm')}.txt`
  )

  element.style.display = 'none'
  document.body.appendChild(element)

  element.click()

  document.body.removeChild(element)
}

export const exportFormatoShipmentXLSX = () => {
  // New sheet book
  const wb = utils.book_new()

  // Destinations data
  const ws_data = []

  // Add header
  ws_data.unshift([
    'Fecha de Creación',
    'Numero de Guía / Tracking aliado',
    'Tipo de retiro',
    'Ciudad Destino',
    'Codigo Tienda destinatario',
    'Tipo de Documento Remitente',
    'Nº de Documento Remitente',
    'Nombre Remitente',
    'Tipo de Documento destinatario',
    'Nº de Documento destinatario',
    'Nombre destinatario',
    'Telefono destinatario',
    'Correo destinatario',
    'Dirección Destino',
    'Codigo Postal',
    'Observaciones',
    'Nº Pieza',
    'Peso en Kg de la pieza',
    'Dimension Alto de la pieza',
    'Dimension Anchode la pieza',
    'Dimension Largo de la pieza',
    'Valor declarado de la pieza',
    'Categoria de la pieza',
  ])

  const ws = utils.aoa_to_sheet(ws_data)

  utils.book_append_sheet(wb, ws, 'FormatoMasivo')

  // Export
  writeFile(wb, `FormatoCargaMasiva_${moment().format('DDMMYYYYHHmm')}.xlsx`)
}

// Export format for Inventory shipments template download
export const exportFormatoShipmentInventoryXLSX = async (
  identificationTypes: {
    taxIdentificationTypeId: string | number
    abbreviationName: string
  }[],
  typeShipment: { value: string | number; name: string }[],
  businessUnits: { buCode: string; buName?: string; buArea?: string }[],
  citiesType: { locationCode: string; locationName: string }[],
  products?: InventoryItemDto[]
) => {
  // Build workbook with ExcelJS to support data validation lists
  const wb = new Workbook()
  const ws = wb.addWorksheet('FormatoInventario')

  // Hidden sheet with lists (referencia y ayuda)
  const lists = wb.addWorksheet('Listas')
  lists.getCell('A1').value = 'CodigoProducto'
  lists.getCell('B1').value = 'NombreProducto'
  const genericProducts =
    products && products.length > 0
      ? products.map((p) => ({ code: p.Id, name: p.Name }))
      : []

  genericProducts.forEach((p, idx) => {
    lists.getCell(`A${idx + 2}`).value = p.code
    lists.getCell(`B${idx + 2}`).value = p.name
  })
  // Asegurar que los códigos de producto se traten como texto (preserva ceros a la izquierda)
  lists.getColumn(1).numFmt = '@'
  // (El rango se referencia directamente en la fórmula usando columnas completas A:B para simplificar)

  // Columna para tipos de documento (ID y Abrev)
  lists.getCell('D1').value = 'TaxIdentificationTypeId'
  lists.getCell('E1').value = 'Abreviacion'
  identificationTypes.forEach((t, idx) => {
    lists.getCell(`D${idx + 2}`).value = t.taxIdentificationTypeId
    lists.getCell(`E${idx + 2}`).value = t.abbreviationName
  })

  // Tipos de entrega
  lists.getCell('G1').value = 'TipoEntregaValor'
  lists.getCell('H1').value = 'TipoEntregaNombre'
  typeShipment.forEach((t, idx) => {
    lists.getCell(`G${idx + 2}`).value = t.value
    lists.getCell(`H${idx + 2}`).value = t.name
  })

  // Tiendas
  lists.getCell('J1').value = 'BuCodeArea'
  lists.getCell('K1').value = 'BuName'
  businessUnits
    .sort((a, b) => (a.buCode || '').localeCompare(b.buCode || ''))
    .forEach((b, idx) => {
      lists.getCell(`J${idx + 2}`).value = `${b.buCode} - ${
        b.buArea?.toUpperCase() || b.buCode?.toUpperCase()
      }`
      lists.getCell(`K${idx + 2}`).value = b.buName || ''
    })

  // Ciudades
  lists.getCell('M1').value = 'CityCode'
  lists.getCell('N1').value = 'CityName'
  citiesType.forEach((c, idx) => {
    lists.getCell(`M${idx + 2}`).value = c.locationCode
    lists.getCell(`N${idx + 2}`).value = c.locationName
  })

  lists.state = 'hidden'

  // Header row
  const headers = [
    'Número de pedido',
    'Tipo de documento destinatario',
    'Número de documento destinatario',
    'Nombre destinatario',
    'Teléfono destinatario',
    'Correo destinatario',
    'Tipo de entrega (retiro por oficina/domicilio)',
    'Código de tienda destino (si es retiro por oficina)',
    'Ciudad/Localidad destino (si es domicilio)',
    'Dirección destino (si es domicilio)',
    'Código postal destino (opcional)',
    'Observaciones (opcional)',
    'Nombre producto',
    'Código producto',
    'Cantidad',
  ]
  ws.addRow(headers)
  ws.getRow(1).font = { bold: true }
  // Columna M (nombre de producto) y N (código de producto) como texto
  ws.getColumn(13).numFmt = '@'
  ws.getColumn(14).numFmt = '@'
  // Add helpful notes to headers
  ws.getCell('B1').note =
    'Seleccione tipo de documento. Se almacenará la abreviación; al importar se mapeará al ID.'
  ws.getCell('G1').note = 'Seleccione tipo de entrega.'
  ws.getCell('H1').note = 'Seleccione código de tienda destino.'
  ws.getCell('I1').note = 'Seleccione ciudad/localidad destino.'
  ws.getCell('M1').note =
    'Seleccione nombre de producto de la lista desplegable.'
  ws.getCell('N1').note =
    'Esta celda se llena automáticamente basado en el nombre de producto seleccionado.'

  // Apply data validation lists over a reasonable number of rows (2..1000)
  // Helper para construir listas en línea (Excel límite ~255 chars)
  const buildInlineList = (values: (string | number)[]) => {
    const clean = values
      .map((v) => (v ?? '').toString().trim())
      .filter((v) => v.length > 0 && !v.includes(','))
    const joined = clean.join(',')
    if (!joined) return null
    if (joined.length <= 255) return `"${joined}"`
    return null // demasiado largo, se omitirá la validación
  }

  const listIdentAbrev = buildInlineList(
    identificationTypes.map((t) => t.abbreviationName)
  )
  const listTipoEntrega = buildInlineList(typeShipment.map((t) => t.name))
  const listTiendas = buildInlineList(
    businessUnits
      .sort((a, b) => (a.buCode || '').localeCompare(b.buCode || ''))
      .map((b) => `${b.buCode} - ${b.buArea || b.buCode}`)
  )
  const listCiudades = buildInlineList(citiesType.map((c) => c.locationName))

  // Rango de respaldo (fallback) para tiendas y ciudades cuando exceden 255 caracteres
  const tiendasRange = businessUnits.length
    ? `=Listas!$J$2:$J$${businessUnits.length + 1}`
    : ''
  const ciudadesRange = citiesType.length
    ? `=Listas!$N$2:$N$${citiesType.length + 1}`
    : ''
  const productosRange = genericProducts.length
    ? `=Listas!$A$2:$A$${genericProducts.length + 1}`
    : ''
  const productosNombresRange = genericProducts.length
    ? `=Listas!$B$2:$B$${genericProducts.length + 1}`
    : ''

  for (let r = 2; r <= 1000; r++) {
    if (listIdentAbrev) {
      ws.getCell(`B${r}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [listIdentAbrev],
        showErrorMessage: true,
        showInputMessage: true,
        promptTitle: 'Tipo documento',
        prompt: 'Seleccione una abreviación',
        errorStyle: 'warning',
        error: 'Valor no válido.',
      }
    }
    if (listTipoEntrega) {
      ws.getCell(`G${r}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [listTipoEntrega],
        showErrorMessage: true,
        showInputMessage: true,
        promptTitle: 'Tipo de entrega',
        prompt: 'Seleccione opción',
        errorStyle: 'stop',
        error: 'Valor no válido.',
      }
    }
    if (listTiendas || tiendasRange) {
      ws.getCell(`H${r}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [
          `=IF(G${r}="Oficina", ${
            listTiendas ? listTiendas : tiendasRange.replace('=', '')
          }, "")`,
        ],
        showErrorMessage: true,
        showInputMessage: true,
        promptTitle: 'Código tienda',
        prompt: 'Seleccione buCode si retiro por oficina',
        errorStyle: 'stop',
        error: 'Opción no válida, Debe seleccionar tipo de entrega Oficina',
      }
    }
    if (listCiudades || ciudadesRange) {
      ws.getCell(`I${r}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [
          `=IF(G${r}="Domicilio", ${
            listCiudades ? listCiudades : ciudadesRange.replace('=', '')
          }, "")`,
        ],
        showErrorMessage: true,
        showInputMessage: true,
        promptTitle: 'Ciudad',
        prompt: 'Seleccione ciudad destino si domicilio',
        errorStyle: 'stop',
        error: 'Opción no válida, Debe seleccionar tipo de entrega Domicilio',
      }
    }
    // Dirección destino solo si Domicilio
    ws.getCell(`J${r}`).dataValidation = {
      type: 'custom',
      allowBlank: true,
      formulae: [`=OR(G${r}="Domicilio", J${r}="")`],
      showErrorMessage: true,
      errorStyle: 'stop',
      error: 'Opción no válida, Debe seleccionar tipo de entrega Domicilio',
    }
    if (productosRange) {
      ws.getCell(`N${r}`).dataValidation = {
        type: 'custom',
        allowBlank: true,
        formulae: [`=FALSE`],
        showErrorMessage: true,
        errorStyle: 'stop',
        error:
          'Esta celda se llena automáticamente basado en el nombre de producto.',
      }
    }
    // Lista desplegable para columna M (Nombre de producto)
    if (productosNombresRange) {
      ws.getCell(`M${r}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [productosNombresRange],
        showErrorMessage: true,
        showInputMessage: true,
        promptTitle: 'Nombre producto',
        prompt: 'Seleccione nombre del producto',
        errorStyle: 'warning',
        error: 'Valor no válido.',
      }
    }
  }

  // Add borders to H, I, J columns
  for (let r = 1; r <= 1000; r++) {
    ws.getCell(`H${r}`).border = {
      top: { style: 'thin', color: { argb: 'FFD8D8D8' } },
      left: { style: 'thin', color: { argb: 'FFD8D8D8' } },
      bottom: { style: 'thin', color: { argb: 'FFD8D8D8' } },
      right: { style: 'thin', color: { argb: 'FFD8D8D8' } },
    }
    ws.getCell(`I${r}`).border = {
      top: { style: 'thin', color: { argb: 'FFD8D8D8' } },
      left: { style: 'thin', color: { argb: 'FFD8D8D8' } },
      bottom: { style: 'thin', color: { argb: 'FFD8D8D8' } },
      right: { style: 'thin', color: { argb: 'FFD8D8D8' } },
    }
    ws.getCell(`J${r}`).border = {
      top: { style: 'thin', color: { argb: 'FFD8D8D8' } },
      left: { style: 'thin', color: { argb: 'FFD8D8D8' } },
      bottom: { style: 'thin', color: { argb: 'FFD8D8D8' } },
      right: { style: 'thin', color: { argb: 'FFD8D8D8' } },
    }
    ws.getCell(`N${r}`).border = {
      top: { style: 'thin', color: { argb: 'FFD8D8D8' } },
      left: { style: 'thin', color: { argb: 'FFD8D8D8' } },
      bottom: { style: 'thin', color: { argb: 'FFD8D8D8' } },
      right: { style: 'thin', color: { argb: 'FFD8D8D8' } },
    }
    ws.getCell(`M${r}`).border = {
      top: { style: 'thin', color: { argb: 'FFD8D8D8' } },
      left: { style: 'thin', color: { argb: 'FFD8D8D8' } },
      bottom: { style: 'thin', color: { argb: 'FFD8D8D8' } },
      right: { style: 'thin', color: { argb: 'FFD8D8D8' } },
    }
  }

  // Conditional formatting to gray out blocked cells
  ws.addConditionalFormatting({
    ref: 'H2:H1000',
    rules: [
      {
        type: 'expression',
        formulae: ['G2<>"Oficina"'],
        priority: 1,
        style: {
          fill: {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF0F0F0' },
          },
        },
      },
    ],
  })
  ws.addConditionalFormatting({
    ref: 'I2:I1000',
    rules: [
      {
        type: 'expression',
        formulae: ['G2<>"Domicilio"'],
        priority: 2,
        style: {
          fill: {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF0F0F0' },
          },
        },
      },
    ],
  })
  ws.addConditionalFormatting({
    ref: 'J2:J1000',
    rules: [
      {
        type: 'expression',
        formulae: ['G2<>"Domicilio"'],
        priority: 3,
        style: {
          fill: {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF0F0F0' },
          },
        },
      },
    ],
  })
  // Gray out column N (read-only)
  ws.addConditionalFormatting({
    ref: 'M2:M1000',
    rules: [
      {
        type: 'expression',
        formulae: ['TRUE'],
        priority: 4,
        style: {
          fill: {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF0F0F0' },
          },
        },
      },
    ],
  })

  // Auto-populate product code (col N) from selected product name (col M)
  for (let r = 2; r <= 1000; r++) {
    // Si hay nombre en M, buscar código correspondiente en N usando INDEX/MATCH
    ws.getCell(`N${r}`).value = {
      formula: `IF(M${r}="", "", IFERROR(INDEX(Listas!$A:$A, MATCH(M${r}, Listas!$B:$B, 0)), ""))`,
    }
  }

  // Forzar recálculo completo al abrir en Excel (forma correcta con ExcelJS)
  wb.calcProperties.fullCalcOnLoad = true

  // Set basic column widths for readability
  const widths = [20, 16, 22, 26, 18, 26, 34, 26, 28, 32, 22, 24, 18, 26, 12]
  ws.columns = widths.map((w) => ({ width: w }))

  // Export as a browser download
  const buffer = await wb.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const href = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = href
  a.download = 'FormatoEnvíoDesdeInventarioV1.xlsx'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(href)
}

//////////////// Export Favorite Destinations
export const exportFavDestinationTXT = (
  favDestinations: FavoriteDestinationsInterfase[]
) => {
  let txt = ''

  for (let i = 0; i < favDestinations.length; i++) {
    const fav = favDestinations[i]

    txt += `"Nombre":"${fav.accountFullName}",\n`
    txt += `"Numero Identificador":${
      fav.taxIDentificationTypeCode + fav.identificationNumber
    },\n`
    txt += `"Teléfono":${fav.phoneNumber},\n`
    txt += `"Email":"${fav.email}",\n`
    txt += `"Dirección":"${fav.addressLine1}",\n`
    txt += i < favDestinations.length - 1 ? ',\n\n' : '\n\n\n'
  }

  let element = document.createElement('a')
  element.setAttribute(
    'href',
    'data:text/plain;charset=utf-8,' + encodeURIComponent(txt)
  )
  element.setAttribute(
    'download',
    `ConsultaDestinosFavoritos_${moment().format('DDMMYYYYHHmm')}.txt`
  )

  element.style.display = 'none'
  document.body.appendChild(element)

  element.click()

  document.body.removeChild(element)
}

export const exportFavDestinationXLSX = (
  favDestinations: FavoriteDestinationsInterfase[]
) => {
  // New sheet book
  const wb = utils.book_new()

  // Destinations data
  const ws_data = favDestinations.map((fav) => [
    fav.accountFullName,
    fav.taxIDentificationTypeCode + fav.identificationNumber,
    fav.phoneNumber,
    fav.email,
    fav.addressLine1,
  ])

  // Add header
  ws_data.unshift([
    'Nombre ',
    'Numero Identificador',
    'Teléfono',
    'Email',
    'Dirección',
  ])

  const ws = utils.aoa_to_sheet(ws_data)

  utils.book_append_sheet(wb, ws, 'Destinations')

  // Export
  writeFile(wb, `ConsultaDespacho_${moment().format('DDMMYYYYHHmm')}.xlsx`)
}

export const exportFavDestinationPDF = (
  user: UserInterface,
  favDestinations: FavoriteDestinationsInterfase[]
) => {
  const doc = new jsPDF()

  // Logo
  let img = new Image()
  img.src = logo
  doc.addImage(img, 'JPEG', 13, 13, 25, 20)

  // Title
  doc.setFontSize(16).setFont('helvetica', 'bold')
  doc.text('REPORTE DE DESTINOS FAVORITOS', 80, 25)
  // Date
  const date = moment()
  doc.setFontSize(10).setFont('helvetica', 'normal')
  doc.text(date.format('DD/MM/YYYY'), 178, 20)
  doc.text(date.format('HH:mm'), 186.5, 24)

  // Destination table
  const columns = [
    'Nombre ',
    'Numero Identificador',
    'Teléfono',
    'Email',
    'Dirección',
  ]
  const rows = favDestinations.map((fav) => [
    fav.accountFullName,
    fav.taxIDentificationTypeCode + fav.identificationNumber,
    fav.phoneNumber,
    fav.email,
    fav.addressLine1,
  ])
  autoTable(doc, {
    head: [columns],
    body: rows,
    margin: { top: 45, bottom: 30 },
    styles: { halign: 'center', valign: 'middle', fontSize: 7 },
    headStyles: {
      fillColor: [0, 100, 172],
      textColor: 255,
      fontStyle: 'bold',
      minCellHeight: 11,
    },
    bodyStyles: {
      minCellHeight: 16,
      lineWidth: 0.1,
      textColor: 'black',
    },
    didDrawPage: (data) => {
      if (data.pageCount > 0) {
        data.settings.margin.top = 15
        doc.setPage(data.pageCount)
      }
    },
  })

  // Footer
  for (let i = 1; i <= doc.getNumberOfPages(); i++) {
    doc.setPage(i)
    doc.text(`Solicitado por: ${user.login}`, 13, 280)
    doc.text(`${i}`, 195, 280)
  }

  doc.save(`ConsultaDestinosFavoritos_${moment().format('DDMMYYYYHHmm')}.pdf`)
}
